import moment from 'moment'

import configProvider from '../../config'
import { addQueryString } from '../../helpers/url'
import { log } from '../../logger'
import { V3Transaction, V3TransactionState } from '../../models/v3-transaction'
import { TransactionMetric } from '../../models/analytics'
import { ChargerRef } from '../../models/charger'
import { Connector } from '../../models/connector'
import { ListingRef } from '../../models/listing'
import { TenantRef } from '../../models/tenant'
import { apiClient } from './client'
import { buildApiManageUrl } from '../../helpers/url'
import { getSession } from '../../state/session-store'
import { getTenant } from '../../state/tenant-store'
import { returnMock } from './mock'
import { GeneralApiResponse } from '../../models/http'
import { MasterAccountRef } from 'models/master-account'
import { User } from 'models/user'

export interface V3TransactionResponse {
  data: V3Transaction[]
  pagination: {
    currentPage: number
    perPage: number
    totalEntries: number
  }
  filters: {
    exportToken?: string
  }
}

export interface V3TransactionFilter {
  page?: number
  defaultTenant?: TenantRef
  tenants?: TenantRef[]
  charger?: ChargerRef
  chargers?: string[]
  serialNumber?: string[]
  connector?: Connector
  startMethod?: string
  state?: V3TransactionState
  transactionId?: string | number
  listing?: ListingRef
  userName?: string
  userEmail?: string
  email?: string
  ocppTransactionId?: string
  listingTitle?: string[]
  masterAccounts?: MasterAccountRef[]
  country?: string[] | string
  province?: string[] | string
  paymentState?: string
  skipTotalCountAndExportToken?: boolean
}

const filterToQueryParams = (
  f: V3TransactionFilter,
  startDate: moment.Moment,
  endDate: moment.Moment,
): { [key: string]: string } => {
  let queryParams: { [key: string]: string } = {
    start: `${startDate.format('DD-MM-YYYY')}`,
    end: `${endDate.format('DD-MM-YYYY')}`,
    is_v3_transaction: 'true',
  }
  if (f.page) {
    queryParams['page'] = `${f.page}`
  }

  if (f.defaultTenant) {
    queryParams['tenant_id'] = `${f.defaultTenant.id}`
  }

  if (f.tenants && f.tenants.length > 0) {
    queryParams['tenant_id'] = `${f.tenants.map((t) => t.id)}`
  }
  if (f.charger) {
    queryParams['charger_id'] = `${f.charger.id}`
  }
  if (f.connector) {
    queryParams['connector_id'] = `${f.connector.id}`
  }
  if (f.startMethod) {
    queryParams['start_method'] = `${f.startMethod}`
  }
  if (f.state) {
    queryParams['state'] = `${f.state}`
  }
  if (f.transactionId) {
    queryParams['transaction_id'] = `${f.transactionId}`
  }
  if (f.listing) {
    queryParams['listing_id'] = `${f.listing.id}`
  }
  if (f.userName) {
    queryParams['user_name'] = `${f.userName}`
  }
  if (f.userEmail || f.email) {
    queryParams['user_email'] = `${f.userEmail || f.email}`
  }
  if (f.ocppTransactionId) {
    queryParams['ocpp_transaction_id'] = `${f.ocppTransactionId}`
  }
  if (f.ocppTransactionId && f.ocppTransactionId.trim()) {
    queryParams['ocpp_transaction_ids'] = `${f.ocppTransactionId.trim()}`
  }
  if (f.listingTitle && f.listingTitle.length > 0) {
    queryParams['listing_title'] = `${f.listingTitle.map((listingTitle) => listingTitle)}`
  }
  if (f.serialNumber && f.serialNumber.length > 0) {
    queryParams['serial_number'] = `${f.serialNumber.map((serial) => serial)}`
  }
  if (f.chargers && f.chargers.length > 0) {
    queryParams['serial_number'] = `${f.chargers.map((serialNumber) => serialNumber)}`
  }
  if (f.masterAccounts && f.masterAccounts.length > 0) {
    queryParams['master_account_ids'] = `${f.masterAccounts.map((masterAccount) => masterAccount.id)}`
  }
  if (f.country) {
    queryParams['country'] = `${f.country}`
  }
  if (f.province) {
    queryParams['province'] = `${f.province}`
  }
  if (f.paymentState) {
    queryParams['payment_state'] = f.paymentState
  }
  if (f.skipTotalCountAndExportToken) {
    queryParams['skip_total_count_and_export_token'] = 'true'
  }

  return queryParams
}

export async function GetV3Transactions(
  currentUser: User | undefined,
  isMockUpEnabled: boolean,
  props: V3TransactionFilter,
  startDate: moment.Moment,
  endDate: moment.Moment,
): Promise<V3TransactionResponse> {
  let url = '/transactions'

  const queryParams = filterToQueryParams({ ...props }, startDate, endDate)
  url = addQueryString(url, queryParams)

  log('fetching v3 transactions', { url })

  let transactions: V3TransactionResponse
  if (isMockUpEnabled && !configProvider.config.callTransactionsApi) {
    // For now, return empty mock data - backend will provide real implementation
    transactions = await Promise.resolve(returnMock({
      data: [],
      pagination: { currentPage: 1, perPage: 10, totalEntries: 0 },
      filters: { exportToken: '' }
    }))
  } else {
    transactions = await apiClient(url, {})
  }
  return Promise.resolve({
    ...transactions,
    data: transactions.data.map((transaction) => ({
      ...transaction,
      tenant: {
        ...transaction.tenant,
        combineName:
          currentUser?.role === 'admin'
            ? transaction.tenant.combineName
            : transaction.tenant.displayName ?? transaction.tenant.name,
      },
    })),
  })
}

export async function GetV3Transaction(transactionId: number): Promise<V3Transaction> {
  let url = `/transactions/${transactionId}`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  log('fetching a v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    // Return mock data - backend will provide real implementation
    return returnMock({} as V3Transaction)
  }
  return apiClient(url, {})
}

/** Meter values response from GET /transactions/:id/meter_values */
export interface V3MeterValueEntry {
  timestamp?: string
  charged_value?: number | string
  value?: number | string
  measurand?: string
  [key: string]: unknown
}

export async function GetV3TransactionMeterValues(
  transactionId: number,
): Promise<V3MeterValueEntry[]> {
  let url = `/transactions/${transactionId}/meter_values`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  log('fetching v3 transaction meter values', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock([])
  }
  return apiClient(url, {})
}

export async function GetV3TransactionsStats(
  isMockUpEnabled: boolean,
  props: V3TransactionFilter,
  startDate: moment.Moment,
  endDate: moment.Moment,
): Promise<TransactionMetric> {
  let url = '/transactions/total'

  const queryParams = filterToQueryParams({ ...props }, startDate, endDate)
  url = addQueryString(url, queryParams)
  log('fetching v3 transactions total', { url })
  if (isMockUpEnabled && !configProvider.config.callTransactionsApi) {
    // Return mock data - backend will provide real implementation
    return returnMock({
      count: 0,
      errorCount: 0,
      cancelCount: 0,
      ongoingCount: 0,
      revenue: 0,
      energy: 0,
      uniqueUsers: 0,
      timestamp: moment(),
      date: moment().format('YYYY-MM-DD'),
      duration: 0,
      humanizeTimestamp: '',
    })
  }
  return apiClient(url, {})
}

export async function StartV3Transaction(transaction: V3Transaction): Promise<V3Transaction> {
  let url = `/transactions/${transaction.id}/start`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  log('remote start v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock({ ...transaction, currentState: 'in_progress' })
  }
  return apiClient(url, { method: 'POST' })
}

export async function ErrorV3Transaction(transaction: V3Transaction, errorReason: string): Promise<V3Transaction> {
  let url = `/transactions/${transaction.id}/error`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  const payload = {
    error_reason: errorReason
  }
  log('remote error v3 transaction', { url, payload })
  if (configProvider.config.mockEnabled) {
    return returnMock({ ...transaction, currentState: 'hardware_error' })
  }
  return apiClient(url, { method: 'POST', body: JSON.stringify(payload) })
}

export async function StopV3Transaction(transaction: V3Transaction): Promise<V3Transaction> {
  let url = `/transactions/${transaction.id}/stop`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  log('remote stop v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock({ ...transaction, currentState: 'completed' })
  }

  const fullUrl = `${buildApiManageUrl()}${url}`
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  const session = getSession()
  if (session) headers['Authorization'] = `Bearer ${session.token}`

  const tenant = getTenant()
  if (tenant) headers['X-Tenant-Id'] = `${tenant.id}`

  const response = await fetch(fullUrl, { method: 'POST', headers: new Headers(headers) })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const message = body?.error?.message || body?.message || 'Failed to stop charging'
    throw new Error(message)
  }

  return response.json()
}

export async function CancelV3PosPayment(transaction: V3Transaction): Promise<V3Transaction> {
  let url = `/transactions/${transaction.id}/cancel_pos_payment`
  url = addQueryString(url, { is_v3_transaction: 'true' })
  log('cancel POS payment for v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock({ ...transaction })
  }
  return apiClient(url, { method: 'POST' })
}

export async function ProceedV3Payment(transaction: V3Transaction): Promise<V3Transaction> {
  const url = `/transactions/${transaction.id}/proceed_payment`
  log('proceed payment for v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock({ ...transaction })
  }
  return apiClient(url, { method: 'POST' })
}

export async function VoidV3Payment(transactionId: number, reason: string): Promise<V3Transaction> {
  const url = `/transactions/${transactionId}/void_payment`
  log('void payment for v3 transaction', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock({} as V3Transaction)
  }
  return apiClient(url, { method: 'POST', body: JSON.stringify({ id: transactionId, reason }) })
}

export interface RefundV3TransactionParams {
  refundType: 'full' | 'partial'
  reason: string
  subtotal?: number
  taxAmount?: number
  otherReason?: string
}

export async function RefundV3Transaction(
  transactionId: number,
  params: RefundV3TransactionParams,
): Promise<{ message: string }> {
  const url = `/transactions/${transactionId}/refund`
  const { refundType, reason, subtotal, taxAmount, otherReason } = params
  // Backend only accepts params[:reason]; when user chose "other", pass their text as reason
  const reasonParam = otherReason ? otherReason : reason
  const requestBody: {
    is_partial_refund: string
    reason: string
    refund_subtotal_cents?: number
    refund_tax_cents?: number
  } = {
    is_partial_refund: refundType === 'partial' ? 'true' : 'false',
    reason: reasonParam,
    ...(refundType === 'partial' && subtotal !== undefined && { refund_subtotal_cents: Math.round(subtotal * 100) }),
    ...(refundType === 'partial' && taxAmount !== undefined && { refund_tax_cents: Math.round(taxAmount * 100) }),
  }
  log('refund v3 transaction', { url, requestBody })
  if (configProvider.config.mockEnabled) {
    return returnMock({ message: 'Successfully refunded transaction' })
  }
  return apiClient(url, { method: 'POST', body: JSON.stringify(requestBody) })
}

export async function BulkSettleV3Transactions(transactionIds: number[]): Promise<GeneralApiResponse> {
  const url = '/transactions/bulk_settle'
  log('bulk settle v3 transactions', { url, transactionIds })
  if (configProvider.config.mockEnabled) {
    return returnMock({ success: true, msg: `${transactionIds.length} transactions marked as settled` })
  }
  return apiClient(url, {
    method: 'POST',
    body: JSON.stringify({ transaction_ids: transactionIds }),
  })
}

